import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { selectAppliedOffers } from '../../store/slices/OfferSlice';
import { selectGiftCard } from '../../store/slices/GiftCardSlice';
import { selectCachedOrder } from '../../store/slices/OrderSlice';
import { COUPON_TYPES, PROMO_CODE_DISCOUNT_TYPES } from '../../services/exports/Constants';
import useOffersTranslations from '../offers/hooks/useOffersTranslations';
import useBasket from '../../hooks/basket/useBasket';
import { DiscountKind } from './useDiscountExclusivity';

export interface ActiveDiscount {
  kind: DiscountKind;
  /** Human label — offer title or the promo code; a generic noun for gift/points. */
  label: string;
  /**
   * The € the currently-applied discount takes off this order, or `null` when it can't be
   * read reliably. Sourced from `cached_order.total_discount` — under full mutual exclusivity
   * (PROD-407) at most one of {offer, promo, gift, points} is applied, so this figure belongs
   * to whichever one `kind` names. Used only to pick the higher-saving "best choice"; the
   * comparison card degrades gracefully (no badge) when it is `null`.
   */
  saving: number | null;
}

/**
 * Resolves the single discount currently applied to the order, if any.
 *
 * Backs the Inc 4 comparison-choice card (FOO-2567): the card compares the active discount
 * against the one the customer is now considering. Read-only — it never mutates state.
 */
export default function useActiveDiscount(): ActiveDiscount | null {
  const { t } = useTranslation(null, { keyPrefix: 'Features:DiscountExclusivity.comparison.kinds' });
  const { getLabelByOffer } = useOffersTranslations();
  const appliedOffers = useSelector(selectAppliedOffers);
  const giftCard = useSelector(selectGiftCard);
  const cachedOrder = useSelector(selectCachedOrder);
  const { items, actualSubtotal } = useBasket();

  // Coupons (promo/gift/points) carry their € in `total_discount`. An offer instead discounts the
  // line items, so its saving is the OFFER-only line delta: `actualSubtotal` (smart-pricing applied,
  // no offers) minus the offer-applied line total (`items`). This excludes smart pricing — using
  // `subtotal − actualSubtotal` would wrongly count smart pricing as offer savings.
  const rawSaving = cachedOrder?.total_discount;
  const couponSaving = typeof rawSaving === 'number' && rawSaving > 0 ? rawSaving : null;
  const itemsActual = (items ?? []).reduce((sum, item) => sum + (item?.actual_price ?? 0), 0);
  const offerSaving = actualSubtotal > itemsActual ? actualSubtotal - itemsActual : null;

  const offer = appliedOffers?.[0];
  if (offer) {
    return { kind: 'offer', label: getLabelByOffer(offer), saving: offerSaving };
  }

  const saving = couponSaving;

  if (cachedOrder?.coupon_type === COUPON_TYPES.PROMO_CODE) {
    // On the menu page `total_discount` is 0, so for a fixed-amount code use its first-order
    // discount (the immediate saving) — that's what a customer would compare against an offer.
    const details = cachedOrder?.coupon_details;
    const firstFixed =
      details?.discount_type === PROMO_CODE_DISCOUNT_TYPES.FIXED_AMOUNT
        ? details?.components?.[0]?.discount
        : undefined;
    const promoSaving = typeof firstFixed === 'number' && firstFixed > 0 ? firstFixed : couponSaving;

    return { kind: 'promo', label: details?.code ?? t('promo'), saving: promoSaving };
  }

  if (giftCard) {
    return { kind: 'gift', label: t('gift'), saving };
  }

  if (cachedOrder?.coupon_type === COUPON_TYPES.CASHBACK && (cachedOrder?.cashback_points ?? 0) > 0) {
    return { kind: 'points', label: t('points'), saving };
  }

  return null;
}
